import { Box, Tab, Tabs } from '@mui/material';
import React from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import PermissionsPage from './pages/permissions/permissionsPage';
import DevicesPage from './pages/devices/devicesPage';
import { User } from './models/user';
import LogsPage from './pages/logs/logsPage';
import UsersPage from './pages/users/usersPage';
import SubscriptionsPage from './pages/subscriptions/subscriptionsPage';
import OwnersPage from './pages/owners/ownersPage';
import ImagesPage from './pages/images/imagesPage';
import DualClock from './components/DualClock';
import ServerHealth from './components/ServerHealth';

interface AppRouterProps {
  user: User
}

function AppRouter(props: AppRouterProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const getTabValue = () => {
    if (location.pathname === '/dashboard') return 0;
    if (location.pathname === '/logs') return 1;
    if (location.pathname === '/permissions') return 2;
    if (location.pathname === '/subscriptions') return 3;
    if (location.pathname === '/owners') return 4;
    if (location.pathname === '/images') return 5;
    if (location.pathname === '/users') return 6;
    return false;
  };

  const isAdmin = props.user.super_admin || props.user.tenant_admin;
  // Platform operator = super_admin with no tenant owner (tenant super_admins exist).
  const isPlatformOperator = props.user.super_admin && !props.user.owner;

  // Page navigation with Shift+PgUp/PgDn and Alt+1/2/3
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Shift+PgUp/PgDn for cycling through pages
      if (e.shiftKey && (e.key === 'PageUp' || e.key === 'PageDown')) {
        e.preventDefault();

        const pages = ['/dashboard', '/logs'];
        if (isAdmin) {
          pages.push('/permissions');
          if (props.user.super_admin) pages.push('/subscriptions');
          if (props.user.super_admin) pages.push('/owners');
          if (props.user.super_admin) pages.push('/images');
          pages.push('/users');
        }

        const currentIndex = pages.indexOf(location.pathname);
        if (currentIndex === -1) return;

        let nextIndex: number;
        if (e.key === 'PageUp') {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : pages.length - 1;
        } else {
          nextIndex = currentIndex < pages.length - 1 ? currentIndex + 1 : 0;
        }

        navigate(pages[nextIndex]);
      }

      // Alt+1/2/3/4/5 for direct page navigation
      if (e.altKey && (e.key === '1' || e.key === '2' || e.key === '3' || e.key === '4' || e.key === '5' || e.key === '6' || e.key === '7')) {
        e.preventDefault();

        if (e.key === '1') {
          navigate('/dashboard');
        } else if (e.key === '2') {
          navigate('/logs');
        } else if (e.key === '3' && isAdmin) {
          navigate('/permissions');
        } else if (e.key === '4' && props.user.super_admin) {
          navigate('/subscriptions');
        } else if (e.key === '5' && props.user.super_admin) {
          navigate('/owners');
        } else if (e.key === '6' && props.user.super_admin) {
          navigate('/images');
        } else if (e.key === '7' && isAdmin) {
          navigate('/users');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [location.pathname, navigate, isAdmin, props.user.super_admin]);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs value={getTabValue()} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          <Tab label="Dashboard" component={Link} to="/dashboard" />
          <Tab label="Logs" component={Link} to="/logs" />
          {isAdmin && <Tab label="Permissions" component={Link} to="/permissions" />}
          {props.user.super_admin && <Tab label="Subscriptions" component={Link} to="/subscriptions" />}
          {props.user.super_admin && <Tab label="Owners" component={Link} to="/owners" />}
          {props.user.super_admin && <Tab label="Images" component={Link} to="/images" />}
          {isAdmin && <Tab label="Users" component={Link} to="/users" />}
        </Tabs>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
          {isPlatformOperator && <ServerHealth />}
          <DualClock />
        </Box>
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Routes>
          <Route path="/dashboard" element={<DevicesPage userId={props.user.uuid} isSuperAdmin={props.user.super_admin} />} />
          <Route path="/logs" element={<LogsPage isSuperAdmin={props.user.super_admin} />} />
          {isAdmin && <Route path="/permissions" element={<PermissionsPage />} />}
          {props.user.super_admin && <Route path="/subscriptions" element={<SubscriptionsPage />} />}
          {props.user.super_admin && <Route path="/owners" element={<OwnersPage />} />}
          {props.user.super_admin && <Route path="/images" element={<ImagesPage />} />}
          {isAdmin && <Route path="/users" element={<UsersPage isSuperAdmin={props.user.super_admin} currentUserOwner={props.user.owner} />} />}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default AppRouter;